import {SurfaceEditor, FileVideoTitleFrameDesignerPhotos, VideoEditorMergeModal} from '#components';
import {makeFilesStore} from '~/stores/files.js';
import {getClipFrameDesignProjectIds} from '~/utils/video-editor/frame-utils.js';
import {markRaw} from 'vue';

export function useVideoEditorActions() {

  async function editCustomFrame({clip, frame = null}) {
    if (useDevice().isMobileOrTablet) {
      const mobileModal = useConfirmModal({
        id: 'surface-editor-mobile-no',
        heading: 'Larger Screen Required',
        message: `The Title Cover Designer isn't available on smaller screens yet. Please switch to a desktop or laptop to use this tool.`,
        confirmText: 'Ok',
        nonCancelable: true,
        confirmMethod: () => mobileModal.close()
      })
      mobileModal.open();
      return;
    }

    if (
      !useSessionStore().isImpersonating &&
      !usePreferencesStore().currentUserPreferences[CURRENT_USER_PREFERENCES.onbVideoTitleFrameDismissed]
    ) {
      await useCommonModals().openVideoTitleFrameWelcomeModal();
    }

    const videoEditorStore = useVideoEditorStore();
    const dayjs = useDayjs();
    const {file} = videoEditorStore;
    const filesStore = makeFilesStore('video-title-frame-designer-files-store')();
    const width = file.width || VIDEO_TITLE_FRAME_DEFAULT_DIMENSIONS.width;
    const height = file.height || VIDEO_TITLE_FRAME_DEFAULT_DIMENSIONS.height;
    const designProjectType = height > width
      ? VIDEO_EDITOR_CUSTOM_FRAME_DESIGN_PROJECT_TEMPLATE_TYPES.portrait
      : VIDEO_EDITOR_CUSTOM_FRAME_DESIGN_PROJECT_TEMPLATE_TYPES.landscape;

    // Use the frame's design_project_id if editing, otherwise null for new
    const projectId = frame?.design_project_id || null;

    useEventBus('core-media-player-stop-playback').emit();

    const customFrameEditorModal = useFModal({
      id: VIDEO_EDITOR_CUSTOM_FRAME_EDITOR_MODAL_ID,
      size: 'fullscreen',
      clickToClose: false,
      showClose: false,
      escToClose: false,
      component: SurfaceEditor,
      props: {
        conf: {
          width,
          height,
          projectType: VIDEO_EDITOR_CUSTOM_FRAME_DESIGN_PROJECT_TYPES,
          designProjectType,
          orientationDesignProjectTypes: VIDEO_EDITOR_CUSTOM_FRAME_DESIGN_PROJECT_TEMPLATE_TYPES,
          exportType: VIDEO_EDITOR_CUSTOM_FRAME_EDITOR_EXPORT_TYPE,
        },
        defaultTheme: SURFACE_EDITOR_COLOR_MODES.dark,
        dynamicTextData: {
          name: clip?.name || file?.name,
          description: file?.description,
          date: file?.taken_at ? dayjs(file.taken_at).format('MMMM D, YYYY') : null,
          ownerName: file?.user?.name,
          clipLength: formatSecondsToVideoEditorTime(getClipLengthSeconds(clip))
        },
        projectId: projectId,
        photosTabComponent: markRaw(FileVideoTitleFrameDesignerPhotos),
        clearFiles: () => {
          filesStore.clearFiles();
        },
        title: `Title Cover for ${clip.name}`
      },
      eventHandlers: {
        projectCreated(project) {
          // Add custom frame to clip when project is created
          const newCustomFrame = {
            design_project_id: project.id,
            duration: VIDEO_TITLE_FRAME_DEFAULT_DURATION
          };
          videoEditorStore.addCustomFrame({
            clip,
            customFrame: newCustomFrame
          });
        }
      },
      onClose() {
        void videoEditorStore.refreshClipCustomFrameSnapshots({clip}).catch(error => {
          console.warn('Failed to refresh clip custom frame thumbnails', error);
        });
      }
    });
    customFrameEditorModal.open();
  }

  async function deleteCustomFrame({clip, frame = null}) {
    const videoEditorStore = useVideoEditorStore();
    const titleFrame = clip?.frames?.title;
    const customFrame = frame || (titleFrame?.design_project_id != null ? titleFrame : null);
    if (!clip || !customFrame) {
      return false;
    }

    const designProjectId = customFrame.design_project_id;
    if (designProjectId !== null && designProjectId !== undefined) {
      await useSurfaceEditorApi().deleteProject({projectId: designProjectId});
    }

    return videoEditorStore.removeCustomFrame({
      clip,
      customFrame,
      designProjectId
    });
  }

  async function deleteClip({clip}) {
    const videoEditorStore = useVideoEditorStore();
    if (!clip) {
      return false;
    }

    const associatedProjectIds = getClipFrameDesignProjectIds(clip);

    for (const projectId of associatedProjectIds) {
      await useSurfaceEditorApi().deleteProject({projectId});
    }

    videoEditorStore.remove({clip});
    return true;
  }

  async function mergeVideos({files, contextType, contextId}) {
    await useSessionStore().getCurrentUser();
    if (useSessionStore().currentUser.isStorageFull) {
      return useCommonModals().openStorageUpsellModal({
        context: STORAGE_UPSELL_CONTEXTS.videoEditorStorageFull
      });
    }

    const filesArr = Array.isArray(files) ? files : [files];

    return new Promise(async (resolve) => {
      await useFModal({
        id: 'merge-videos-modal',
        clickToClose: false,
        size: 'md',
        component: VideoEditorMergeModal,
        props: {
          files: filesArr,
          contextType,
          contextId
        },
        eventHandlers: {
          success() {
            resolve(true);
          }
        }
      }).open();
    });
  }

  return {
    editCustomFrame,
    deleteCustomFrame,
    deleteClip,
    mergeVideos
  };
}
